import { Component, useRef, type ErrorInfo, type ReactElement, type ReactNode } from 'react';
import { readBootstrap } from './types/bootstrap';
import { LegalReviewPage } from './modules/legal/LegalReviewPage';
import { EmberApp } from './modules/ember/EmberApp';
import { IdentifiedLinkConsumer, SessionProvider } from './modules/auth';
import { ToastHost } from './design-system/components';
import { track, trackError } from '../../shared-web/track';
import { CookieBanner, type ConsentRegime } from '../../shared-web/CookieBanner';
import { forgetAdCookies } from '../../shared-web/cookie-consent';
import type { EmberSsrData } from './modules/ember/api';
import { SsrLocationProvider, useRoute, type RouteLocation } from './modules/ember/router';

class AppErrorBoundary extends Component<{ children: ReactNode }, { message: string }> {
  state = { message: '' };

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    const message = error instanceof Error ? error.message : String(error);
    this.setState({ message });
    trackError('render_failed', error instanceof Error ? error.name : typeof error, {
      route: readBootstrap().route,
      message: message.slice(0, 300),
      stack: (error instanceof Error ? String(error.stack || '') : '').slice(0, 500),
      component_stack: String(info.componentStack || '').slice(0, 500),
    });
    if (typeof console !== 'undefined') {
      console.error('Ember SPA render failed', error, info);
    }
  }

  render(): ReactNode {
    if (this.state.message) {
      return (
        <div style={{ padding: 24, color: 'var(--semantic-error)' }}>
          Page failed to render: {this.state.message}
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Top-level shell. Sets up session context (reads cached token,
 * verifies via /api/me, exposes useSession()) and consumes any
 * `?_a=` deep-link token left in the URL by an outbound message.
 *
 * The route dispatcher reads server-injected window.__ORI_BOOTSTRAP__
 * to decide which page to render. If no bootstrap is present (e.g.,
 * direct visit to /dist/index.html in dev), falls back to the demo.
 */
function RouteSwitch({ initialSsrData }: { initialSsrData?: EmberSsrData | null }): ReactElement {
  const boot = readBootstrap();
  if (initialSsrData) {
    return <EmberApp initialSsrData={initialSsrData} />;
  }
  switch (boot.route) {
    case 'ember':
      // Ember v1.2 product (新设计文档 §2): one bootstrap route, client-side
      // router inside. Public pages render anonymous; gated ones redirect.
      return <EmberApp />;
    case 'legal-review':
      return <LegalReviewPage />;
    case 'unknown':
    default:
      return <NotFoundPage />;
  }
}

/**
 * Generic landing for any bootstrap the SPA doesn't recognise — e.g.
 * a stale share link, a workspace whose owner just deleted their
 * account, or someone fishing the URL space. Replaces the previous
 * fallback to <PlanDemoPage />, which surfaced a fake-looking
 * itinerary to anyone who landed on an unmapped route. The CTA
 * routes them to oriscen.com so they have a real next step.
 */
function NotFoundPage(): ReactElement {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 24px',
        background: 'var(--brand-off-white)',
        color: 'var(--brand-dark)',
        gap: 14,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 44,
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        This page is gone
      </div>
      <div
        style={{
          fontSize: 15,
          color: 'var(--dark-400)',
          maxWidth: 320,
          lineHeight: 1.5,
        }}
      >
        The link doesn't go anywhere we recognise — it may have been removed or the account deleted.
      </div>
      <a
        href="https://oriscen.com/"
        style={{
          marginTop: 12,
          display: 'inline-block',
          padding: '12px 22px',
          borderRadius: 999,
          background: 'var(--brand-highlight)',
          color: 'var(--brand-dark)',
          fontWeight: 600,
          fontSize: 15,
          textDecoration: 'none',
        }}
      >
        Go to oriscen.com
      </a>
    </div>
  );
}

/**
 * The consent banner, raised on the homepage and nowhere else (拍板 2026-08-25).
 *
 * It used to hang off the shell, so every SPA route carried it. The homepage is
 * where the answer belongs: it is the surface a visitor lands on, it is the one
 * page that renders the footer — and the footer's "Cookie preferences" is the
 * standing way back in, so the question and the way to reopen it now live on the
 * same page instead of one being reachable without the other.
 *
 * What this consciously accepts: the ad pixels are a property of the bundle, not
 * of the route — index.html starts them on load, and for an undecided US visitor
 * that happens before any React renders. Someone who lands straight on a story,
 * a theme, or a claim page is therefore tracked with no exit offered on that
 * page; only a trip through the homepage raises the question. A stored answer
 * still binds everywhere (the head snippet reads it on every load), and Reject
 * still reaches backwards through forgetAdCookies.
 *
 * The route drives mount/unmount, so the banner leaves when the visitor
 * navigates off the homepage and returns if they come back. `shownRef` keeps the
 * telemetry honest across that: one banner_shown per page load, not one per
 * visit to the homepage — the accept rate's denominator is visitors asked, and a
 * remount is not a second asking.
 */
function HomepageCookieBanner({ deferUntilMounted }: { deferUntilMounted: boolean }): ReactElement | null {
  const loc = useRoute();
  const shownRef = useRef(false);
  // Which sentence the banner may claim. The head snippet in index.html makes
  // the same call the marketing bundle does: for an undecided US visitor the
  // pixels start before the banner renders (US law reads silence as permission
  // and asks for an exit), and only then is the regime "opt_out" — so the copy
  // the visitor reads always matches what is actually running.
  const consentRegime: ConsentRegime = typeof window !== 'undefined'
    && (window as { __oriConsentRegime?: ConsentRegime }).__oriConsentRegime === 'opt_out'
    ? 'opt_out'
    : 'opt_in';
  if (loc.route.name !== 'landing') return null;
  return (
    <CookieBanner
      regime={consentRegime}
      onShown={() => {
        if (shownRef.current) return;
        shownRef.current = true;
        track('consent.banner_shown', {});
      }}
      onDecision={(decision) => track('consent.decided', { decision })}
      // The visitor just opted in — start the ad pixels (defined by the
      // index.html head snippet) without a reload. An SPA may never
      // full-load again this session, so without this call an Accept
      // would not take effect until some later visit: no _fbp/_ttp gets
      // minted, and every server-side conversion for this browser is
      // silently skipped for lack of a match key.
      onAccept={() => window.__oriLoadAdPixels?.()}
      // Registration can complete while this bundle is on screen, so a
      // rejection here has to delete the match keys before the very next
      // request can copy them onto the new person. The server clears its
      // own copy off the consent.decided event (events-routes.js).
      onReject={() => forgetAdCookies()}
      deferUntilMounted={deferUntilMounted}
    />
  );
}

export function App({
  initialLocation,
  initialSsrData,
  ssrActorId,
}: {
  initialLocation?: RouteLocation;
  initialSsrData?: EmberSsrData | null;
  /** Server render only: the signed-in actor this document is minted for.
   *  The client reads the same id from bootstrap, so both sides start the
   *  SessionProvider at 'authenticated' and the first paint agrees. Rendering
   *  the server side without it left the nav empty (status 'loading') while a
   *  signed-in client hydrated the AccountMenu into that slot — React #418/#423
   *  on every signed-in homepage load (prod 2026-09-03). */
  ssrActorId?: string;
} = {}): ReactElement {
  const boot = readBootstrap();
  const hydratableHomepage = initialSsrData?.page === 'home'
    || (boot.route === 'ember' && boot.ssr?.page === 'home');
  // The server stamps the actor into bootstrap on EVERY page it serves
  // signed-in (renderEmberPage: authed/actorId) — not only the onboarding
  // case that also pre-mints a sessionToken. Gating this on sessionToken
  // meant an ordinary signed-in page load handed the SessionProvider
  // nothing, so first paint ran as maybe-anonymous for one /api/me
  // round-trip: the guest consent card flashed over /talk?mode=chat and the
  // trial door over /collect (prod 2026-09-04).
  const bootActorId = ssrActorId || (boot as { actorId?: string }).actorId || '';
  const bootstrapSession = bootActorId
    ? {
      session_token: (boot as { sessionToken?: string }).sessionToken || '',
      actor_id: bootActorId,
      self_name: (boot as { selfName?: string }).selfName || '',
    }
    : null;
  return (
    <AppErrorBoundary>
      <SsrLocationProvider value={initialLocation ?? null}>
        <SessionProvider bootstrapSession={bootstrapSession}>
          <IdentifiedLinkConsumer />
          <RouteSwitch initialSsrData={initialSsrData} />
          <ToastHost />
          <HomepageCookieBanner deferUntilMounted={hydratableHomepage} />
        </SessionProvider>
      </SsrLocationProvider>
    </AppErrorBoundary>
  );
}
